import React, { useEffect, useState } from 'react';
import {
  Dialog, Button, Field, TextInput, Select, RadioList, Banner, Spinner,
} from '@NP-Webber/crm-kit/dist/next/index.js';
import api from '../services/api.js';
import { GemaraSelectionEditorNext } from './GemaraNext.jsx';
import { selectionToEntries, entriesToSelection, isGemaraEntriesValid } from './gemaraSelectionModel.js';
import { READER_ID, useBarcodeCamera } from './barcodeCamera.js';
import { Row } from '../next/ui';

/**
 * Scan flow in the new design (labels screen): camera panel, "mark examined" confirm and grade entry after a scan.
 * Same props and server calls as BarcodeCameraPanel / ScanExamineConfirmDialog / ScanGradeDialog.
 */
const IS_OPEN_GEMARA = process.env.REACT_APP_EXAM_MODE === 'open-gemara';

const memberLabel = (member) => {
  if (!member) return '';
  const name = `${member.firstName || ''} ${member.lastName || ''}`.trim();
  const father = member.fatherName ? ` בן ${member.fatherName}` : '';
  return `${name}${father}`.trim() || `קוד ${member.personalCode}`;
};
const examLabel = (exam, gemaraSelection, withRanges = true) => {
  if (exam) return `${exam.code != null ? `[${exam.code}] ` : ''}${exam.name || ''}${exam.material ? ` — ${exam.material}` : ''}`.trim();
  if (gemaraSelection?.length) {
    return gemaraSelection.map((s) => {
      const ranges = withRanges ? (s.ranges || []).map((r) => `${r.from}–${r.to}`).join(', ') : '';
      return ranges ? `${s.tractate} (${ranges})` : s.tractate;
    }).filter(Boolean).join(', ');
  }
  return 'ללא מבחן';
};

function MemberHead({ member, selected, single, showExam = true }) {
  return (
    <div className="nx-stack" style={{ gap: 2 }}>
      <b style={{ fontSize: 16 }}>{memberLabel(member)}</b>
      {member?.personalCode != null ? <small className="ck-muted">{`קוד אישי: ${member.personalCode}`}</small> : null}
      {single && selected ? (
        <>
          {showExam ? <span><b>{examLabel(selected.exam, selected.gemaraSelection, false)}</b></span> : null}
          {selected.period?.name ? <small className="ck-muted">{`זמן: ${selected.period.name}`}</small> : null}
        </>
      ) : null}
    </div>
  );
}

export function BarcodeCameraPanelNext({ active, paused = false, onScan, onStop }) {
  const { error, starting } = useBarcodeCamera({ active, paused, onScan });
  if (!active) return null;
  return (
    <div className="nx-stack" style={{ gap: 8 }}>
      <Row justify="space-between">
        <small className="ck-muted">{paused ? 'סריקה מושהית — לאחר סגירת החלון תמשיך אוטומטית' : 'כוונו את הברקוד למרכז. סריקה רציפה — אפשר לסרוק אחד אחרי השני'}</small>
        <Button size="sm" variant="ghost" icon="x" onClick={onStop}>עצור מצלמה</Button>
      </Row>
      {error ? <Banner tone="danger" icon="alert">{error}</Banner> : null}
      {starting && !error ? <Row><Spinner size={16} /><small className="ck-muted">מפעיל מצלמה…</small></Row> : null}
      <div id={READER_ID} className={`nx-camera ${paused ? 'is-paused' : ''}`} />
    </div>
  );
}

export function ScanExamineConfirmDialogNext({ open, onClose, member, candidates = [], onConfirm, saving = false }) {
  const [selectedRegId, setSelectedRegId] = useState('');
  const [entries, setEntries] = useState([]);
  const [error, setError] = useState('');
  const selected = candidates.find((c) => c.registrationId === selectedRegId) || candidates[0];
  const gemara = selected?.gemaraSelection || [];
  const isGemara = gemara.length > 0;

  useEffect(() => {
    if (!open) return;
    setError('');
    setSelectedRegId(candidates[0]?.registrationId || '');
    setEntries(selectionToEntries(candidates[0]?.gemaraSelection || []));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  useEffect(() => {
    setEntries(selected ? selectionToEntries(selected.gemaraSelection || []) : []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRegId]);

  const confirm = () => {
    setError('');
    if (!selected?.registrationId) { setError('לא נמצאה הרשמה לסימון'); return; }
    if (isGemara) {
      if (!isGemaraEntriesValid(entries)) { setError('יש למלא טווחי דפים תקינים לכל מסכת'); return; }
      const gemaraSelection = entriesToSelection(entries);
      if (!gemaraSelection.some((g) => g.examined)) { setError('יש לסמן לפחות מסכת אחת כנבחן'); return; }
      onConfirm?.({ registrationId: selected.registrationId, gemaraSelection });
      return;
    }
    onConfirm?.({ registrationId: selected.registrationId, examined: true });
  };
  const alreadyExamined = isGemara ? gemara.every((s) => s.examined) : !!selected?.examined;

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      title="אישור סימון נבחן"
      icon="check"
      size="md"
      footer={(
        <>
          <Button variant="ghost" disabled={saving} onClick={onClose}>ביטול</Button>
          <Button variant="primary" icon="check" disabled={saving || !selected?.registrationId || (isGemara && !isGemaraEntriesValid(entries))} onClick={confirm}>{saving ? 'שומר…' : 'סמן כנבחן'}</Button>
        </>
      )}
    >
      <div className="nx-stack">
        <MemberHead member={member} selected={selected} single={candidates.length === 1} showExam={!isGemara} />
        {candidates.length > 1 ? (
          <Field label="בחירת הרשמה">
            <RadioList value={selectedRegId} onChange={setSelectedRegId} options={candidates.map((c) => ({ value: c.registrationId, label: examLabel(c.exam, c.gemaraSelection) }))} />
          </Field>
        ) : null}
        {isGemara ? (
          <>
            <p className="nx-desc">ערוך את טווחי הדפים וסמן את המסכתות שהנרשם נבחן בהן:</p>
            <GemaraSelectionEditorNext value={entries} onChange={setEntries} showExamined />
          </>
        ) : null}
        {alreadyExamined ? <Banner tone="info" icon="info">כבר מסומן כנבחן — אישור יעדכן שוב את הסימון.</Banner> : null}
        {error ? <Banner tone="danger" icon="alert">{error}</Banner> : null}
      </div>
    </Dialog>
  );
}

export function ScanGradeDialogNext({ open, onClose, member, candidates = [], onSaved }) {
  const [selectedRegId, setSelectedRegId] = useState('');
  const [tractate, setTractate] = useState('');
  const [score, setScore] = useState('');
  const [existingGrade, setExistingGrade] = useState(null);
  const [loadingGrade, setLoadingGrade] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const selected = candidates.find((c) => c.registrationId === selectedRegId) || candidates[0];
  const gemara = selected?.gemaraSelection || [];
  const isGemara = IS_OPEN_GEMARA || gemara.length > 0;

  useEffect(() => {
    if (!open) return;
    setError('');
    setScore('');
    setExistingGrade(null);
    setSelectedRegId(candidates[0]?.registrationId || '');
    setTractate(candidates[0]?.gemaraSelection?.[0]?.tractate || '');
  }, [open, candidates]);

  useEffect(() => {
    if (!open || !member?._id || !selected) return undefined;
    let cancelled = false;
    (async () => {
      setLoadingGrade(true);
      setExistingGrade(null);
      try {
        const params = { memberId: member._id, all: 1 };
        if (selected.exam?._id) params.examId = selected.exam._id;
        if (selected.periodId) params.examPeriodId = selected.periodId;
        const { data } = await api.get('/grades', { params });
        const want = isGemara ? (tractate || null) : null;
        const match = (data?.data || []).find((g) => (g.tractate || null) === want);
        if (!cancelled) { setExistingGrade(match || null); setScore(match ? String(match.score ?? '') : ''); }
      } catch {
        if (!cancelled) setExistingGrade(null);
      } finally {
        if (!cancelled) setLoadingGrade(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, member?._id, selectedRegId, tractate]);

  const save = async () => {
    setError('');
    if (!member?._id) { setError('חסר חבר'); return; }
    if (!selected) { setError('לא נמצאה הרשמה'); return; }
    if (isGemara && gemara.length > 0 && !tractate) { setError('יש לבחור מסכת'); return; }
    const s = parseFloat(score);
    if (Number.isNaN(s) || s < 0) { setError('ציון לא תקין (חייב להיות 0 ומעלה)'); return; }
    setSaving(true);
    try {
      if (existingGrade?._id) {
        await api.put(`/grades/${existingGrade._id}`, { score: s, tractate: isGemara ? (tractate || null) : null, version: existingGrade.version });
      } else {
        await api.post('/grades', selected.exam?._id
          ? { memberId: member._id, examId: selected.exam._id, examPeriodId: selected.periodId || selected.exam.periodId || null, score: s, tractate: isGemara ? (tractate || null) : null }
          : { memberId: member._id, examPeriodId: selected.periodId, score: s, tractate: tractate || null });
      }
      onSaved?.();
      onClose?.();
    } catch (e) {
      setError(e.response?.data?.error || 'שגיאת שמירה');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      title="הזנת ציון"
      icon="task"
      size="sm"
      footer={(
        <>
          <Button variant="ghost" disabled={saving} onClick={onClose}>ביטול</Button>
          <Button variant="primary" disabled={saving || loadingGrade || !selected} onClick={save}>{saving ? 'שומר…' : 'שמור ציון'}</Button>
        </>
      )}
    >
      <div className="nx-stack">
        <MemberHead member={member} selected={selected} single={candidates.length === 1} />
        {candidates.length > 1 ? (
          <Field label="בחירת מבחן / הרשמה">
            <RadioList
              value={selectedRegId}
              onChange={(v) => { setSelectedRegId(v); setTractate(candidates.find((c) => c.registrationId === v)?.gemaraSelection?.[0]?.tractate || ''); setScore(''); }}
              options={candidates.map((c) => ({ value: c.registrationId, label: examLabel(c.exam, c.gemaraSelection, false) }))}
            />
          </Field>
        ) : null}
        {isGemara && gemara.length > 0 ? (
          <Field label="מסכת"><Select value={tractate} onChange={(v) => { setTractate(v); setScore(''); }} options={gemara.map((sel) => ({ value: sel.tractate, label: sel.tractate }))} /></Field>
        ) : null}
        {loadingGrade ? <div className="ck-empty"><Spinner size={22} /></div> : (
          <Field label={existingGrade ? 'ציון (קיים — יעודכן)' : 'ציון'}>
            <TextInput type="number" ltr min={0} step={1} value={score} data-autofocus onChange={(e) => setScore(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') save(); }} />
          </Field>
        )}
        {error ? <Banner tone="danger" icon="alert">{error}</Banner> : null}
      </div>
    </Dialog>
  );
}
